import React, { useEffect, useRef, useState } from 'react';
import '../styles/styleTwo.css';

interface CarouselProps {
  items: string[];
  autoPlay?: boolean;
  autoPlayDelay?: number;
  loop?: boolean;
  showDots?: boolean;
  showNav?: boolean;
  slideSpeed?: number;
  responsive?: { [breakpoint: number]: number };
  animationType?: 'fade' | 'slideLeft' | 'slideRight' | 'zoom' | 'flip';
  clone?: boolean;
  customNavButtons?: {
    prev: React.ReactNode;
    next: React.ReactNode;
  };
  customDots?: (index: number, isActive: boolean, onClick: () => void) => React.ReactNode;
  onSlideChange?: (index: number) => void;
  onSlideChangeStart?: (index: number) => void;
  onSlideChangeEnd?: (index: number) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onClickSlide?: (index: number) => void;
  onAutoplayStart?: () => void;
  onAutoplayStop?: () => void;
  onVisibleSlidesChange?: (visibleIndices: number[]) => void;
  onCarouselFocus?: () => void;
  onCarouselBlur?: () => void;
  onImageError?: (index: number, error: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}

const ReactCarousal: React.FC<CarouselProps> = ({
  items,
  autoPlay = true,
  autoPlayDelay = 3000,
  loop = true,
  showDots = true,
  showNav = true,
  slideSpeed = 500,
  responsive = { 0: 1, 768: 2, 1024: 3 },
  animationType = 'slideLeft',
  clone = false,
  customNavButtons,
  customDots,
  onSlideChange,
  onSlideChangeStart,
  onSlideChangeEnd,
  onDragStart,
  onDragEnd,
  onClickSlide,
  onAutoplayStart,
  onAutoplayStop,
  onVisibleSlidesChange,
  onCarouselFocus,
  onCarouselBlur,
  onImageError,
}) => {
  const [itemsPerView, setItemsPerView] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(clone ? itemsPerView - 1 : 0);
  const [isAutoplaying, setIsAutoplaying] = useState(autoPlay);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragDistance = useRef(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const totalItems = items.length;
  const clonesBefore = loop ? items.slice(-itemsPerView) : [];
  const clonesAfter = loop ? items.slice(0, itemsPerView) : [];
  const clonedItems = [...clonesBefore, ...items, ...clonesAfter];

  const handleTransitionEnd = () => {
    if (!loop) {
      setIsTransitioning(false);
      onSlideChangeEnd?.(currentIndex);
      return;
    }

    setIsTransitioning(false);
    let newIndex = currentIndex;
    if (currentIndex === 0) {
      newIndex = totalItems;
    } else if (currentIndex === totalItems + itemsPerView) {
      newIndex = itemsPerView;
    }
    setCurrentIndex(newIndex);
    onSlideChangeEnd?.(newIndex);
  };

  const getAnimation = (type: CarouselProps['animationType'], speed: number) => {
    const duration = speed / 10000;

    switch (type) {
      case 'fade':
        return {
          initial: { opacity: 0 },
          animate: { opacity: 1, transition: { duration } },
          exit: { opacity: 0 },
        };
      case 'slideLeft':
        return {
          initial: { x: '100%' },
          animate: { x: '0%', transition: { duration } },
          exit: { x: '-100%' },
        };
      case 'slideRight':
        return {
          initial: { x: '-100%' },
          animate: { x: '0%', transition: { duration } },
          exit: { x: '100%' },
        };
      case 'zoom':
        return {
          initial: { opacity: 0, scale: 0.8 },
          animate: { opacity: 1, scale: 1, transition: { duration } },
          exit: { opacity: 0, scale: 0.8 },
        };
      case 'flip':
        return {
          initial: { opacity: 0, rotateY: 90 },
          animate: { opacity: 1, rotateY: 0, transition: { duration } },
          exit: { opacity: 0, rotateY: -90 },
        };
      default:
        return {
          initial: { opacity: 0, x: 50 },
          animate: { opacity: 1, x: 0, transition: { duration } },
          exit: { opacity: 0, x: -50 },
        };
    }
  };

  // Responsive items per view
  useEffect(() => {
    const updateItemsPerView = () => {
      const width = window.innerWidth;
      const breakpoints = Object.keys(responsive)
        .map(Number)
        .sort((a, b) => a - b);

      for (let i = breakpoints.length - 1; i >= 0; i--) {
        if (width >= breakpoints[i]) {
          const newItemsPerView = responsive[breakpoints[i]];
          setItemsPerView(newItemsPerView);
          onVisibleSlidesChange?.(
            Array.from({ length: newItemsPerView }, (_, i) => currentIndex + i)
          );
          return;
        }
      }
      setItemsPerView(1);
      onVisibleSlidesChange?.([currentIndex]);
    };

    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, [responsive, currentIndex, onVisibleSlidesChange]);

  const nextSlide = () => {
    setIsTransitioning(true);
    onSlideChangeStart?.(currentIndex + 1);
    setCurrentIndex((prev) => {
      if (!loop && prev >= totalItems - itemsPerView) return prev;
      return prev + 1;
    });
  };

  const prevSlide = () => {
    setIsTransitioning(true);
    onSlideChangeStart?.(currentIndex - 1);
    setCurrentIndex((prev) => {
      if (!loop && prev <= 0) return prev;
      return prev - 1;
    });
  };

  // Autoplay
  useEffect(() => {
    if (!autoPlay || !isAutoplaying) return;

    onAutoplayStart?.();
    timeoutRef.current = setTimeout(() => {
      nextSlide();
    }, autoPlayDelay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        onAutoplayStop?.();
      }
    };
  }, [currentIndex, autoPlay, autoPlayDelay, isAutoplaying, onAutoplayStart, onAutoplayStop]);

  // Trigger onSlideChange and onVisibleSlidesChange
  useEffect(() => {
    onSlideChange?.(currentIndex);
    onVisibleSlidesChange?.(
      Array.from({ length: itemsPerView }, (_, i) => currentIndex + i)
    );
  }, [currentIndex, itemsPerView, onSlideChange, onVisibleSlidesChange]);

  // Drag handling
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    onDragStart?.();
    setIsAutoplaying(false);
    dragStartX.current =
      'touches' in e ? e.touches[0].clientX : e.clientX;
    dragDistance.current = 0;
  };

  const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;
    const currentX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    dragDistance.current = dragStartX.current - currentX;
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    onDragEnd?.();
    const threshold = 50; // Pixels to trigger slide change
    if (Math.abs(dragDistance.current) > threshold) {
      if (dragDistance.current > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
    dragDistance.current = 0;
  };

  // Click handling
  const handleSlideClick = (index: number) => {
    if (Math.abs(dragDistance.current) < 10) { // Small threshold to differentiate from drags
      onClickSlide?.(index);
    }
  };

  // Focus handling
  const handleFocus = () => {
    onCarouselFocus?.();
  };

  const handleBlur = () => {
    onCarouselBlur?.();
  };

  const handleImageError = (index: number, error: React.SyntheticEvent<HTMLImageElement, Event>) => {
    onImageError?.(index, error);
  };

  const animation = getAnimation(animationType, slideSpeed);

  return (
    <div
      className="react-carousel"
      ref={carouselRef}
      tabIndex={0}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseDown={handleDragStart}
      onMouseMove={handleDragMove}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
      onTouchStart={handleDragStart}
      onTouchMove={handleDragMove}
      onTouchEnd={handleDragEnd}
    >
      <div
        className="carousel-track"
        onTransitionEnd={handleTransitionEnd}
        style={{
          width: `${clonedItems.length * (100 / itemsPerView)}%`,
          transform: `translateX(-${(currentIndex * 100) / clonedItems.length}%)`,
          transition: isTransitioning ? `transform ${slideSpeed}ms ease-in-out` : 'none',
        }}
      >
        {clonedItems.map((item, index) => {
          const isActive = index >= currentIndex && index < currentIndex + itemsPerView;
         
          return (
            <div
              key={index}
              className={`carousel-slide ${isActive ? 'active' : ''}`}
              style={{ width: `${100 / items.length}%` }}
              onClick={() => handleSlideClick(index)}
            >
              <p>{ currentIndex + 1}</p>
              <img
                src={item}
                alt={`Slide ${index}`}
                className="carousel-image"
                onError={(e) => handleImageError(index, e)}
              />
            </div>
          );
        })}
      </div>

      {/* Navigation */}
      {showNav && (
        <>
          {customNavButtons ? (
            <>
              <div className="nav-btn left" onClick={prevSlide}>
                {customNavButtons.prev}
              </div>
              <div className="nav-btn right" onClick={nextSlide}>
                {customNavButtons.next}
              </div>
            </>
          ) : (
            <>
              <button className="nav-btn left" onClick={prevSlide}>
                {"<"}
              </button>
              <button className="nav-btn right" onClick={nextSlide}>
                {">"}
              </button>
            </>
          )}
        </>
      )}

      {/* Dots */}
      {showDots && (
        <div className="carousel-dots">
          {Array.from({ length: Math.ceil(items.length / itemsPerView) }).map((_, i) => {
            const isActive = i === Math.floor(currentIndex / itemsPerView);
             console.log("isActive", isActive)
            return customDots ? (
              <div key={i} onClick={() => setCurrentIndex(i * itemsPerView)}>
                {customDots(i, isActive, () => setCurrentIndex(i * itemsPerView))}
              </div>
            ) : (
              <button
                key={i}
                className={`dot ${isActive ? 'active' : ''}`}
                onClick={() => setCurrentIndex(i * itemsPerView)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReactCarousal;